/**
 * Single source of truth for every `name` value sent through `useSendEventLog`.
 *
 * Why this exists: ClickHouse queries and dashboards rely on a finite, knowable
 * set of `timeout_name` strings. Hard-coded names spread across 25+ callsites
 * with template-literal interpolation make that impossible. This registry
 * collapses them all into one place — `grep -r EVENT_NAMES client/src` lists
 * every shipped event-log name.
 *
 * Conventions:
 *  - Screens / pages / actions: `lowercase-kebab`
 *  - Provider names: preserved casing (proper nouns — Apple, MyPos, MiaPay)
 *  - One canonical key per screen (used by hooks and screenLog)
 */

import { IS_DEV } from '../../shared/constants';

export const EVENT_NAMES = {
    // --- Dynamic families: keyed by a typed identifier ---
    cancel: {
        'snack':           'cancel - snack',
        'water':           'cancel - water',
        'pulse':           'cancel - pulse',
        'car-wash':        'cancel - car-wash',
        'payment':         'cancel - payment',
        'payment-ct5':     'cancel - payment-ct5',
        'payment-ct10':    'cancel - payment-ct10',
        'payment-popcorn': 'cancel - payment-popcorn',
        'connecting':      'cancel - connecting',
        'select':          'cancel - select',
    },
    amountSubmitted: {
        'water':    'amount submitted - water',
        'pulse':    'amount submitted - pulse',
        'car-wash': 'amount submitted - car-wash',
        'snack':    'amount submitted - snack',
        'ct5':      'amount submitted - ct5',
        'ct10':     'amount submitted - ct10',
    },
    paymentInitiated: {
        Apple:   'payment initiated - Apple',
        Google:  'payment initiated - Google',
        MyPos:   'payment initiated - MyPos',
        TBC:     'payment initiated - TBC',
        MiaPay:  'payment initiated - MiaPay',
        Loyalty: 'payment initiated - Loyalty',
    },
    paymentFailed: {
        TBC:     'payment failed - TBC',
        MiaPay:  'payment failed - MiaPay',
        Loyalty: 'payment failed - Loyalty',
    },
    paymentFailedNoUri: {
        TBC:    'payment failed - TBC - no uri',
        MiaPay: 'payment failed - MiaPay - no uri',
    },
    paymentSuccess: {
        Loyalty: 'payment success - Loyalty',
    },
    // --- Why the Google Pay sheet closed without a token (SWRN-2089), keyed by
    // Google's `PaymentsError.statusCode`. `UNKNOWN` covers errors without a
    // code, e.g. pay.js failing to load. ---
    googlePaySheetClosed: {
        CANCELED:               'google pay sheet closed - CANCELED',
        BUYER_ACCOUNT_ERROR:    'google pay sheet closed - BUYER_ACCOUNT_ERROR',
        DEVELOPER_ERROR:        'google pay sheet closed - DEVELOPER_ERROR',
        MERCHANT_ACCOUNT_ERROR: 'google pay sheet closed - MERCHANT_ACCOUNT_ERROR',
        INTERNAL_ERROR:         'google pay sheet closed - INTERNAL_ERROR',
        UNKNOWN:                'google pay sheet closed - UNKNOWN',
    },
    // --- Page-hidden (SWRN-1639, A3): emitted via sendBeacon when the tab is
    // backgrounded/closed, keyed by the screen the user was on. ---
    pageHidden: {
        'connecting':   'page hidden - connecting',
        'select':       'page hidden - select product',
        'snack':        'page hidden - snack',
        'water':        'page hidden - water',
        'pulse':        'page hidden - pulse',
        'car-wash':     'page hidden - car-wash',
        'popcorn':      'page hidden - popcorn',
        'payment':      'page hidden - payment',
        'payment-ct5':  'page hidden - payment-ct5',
        'payment-ct10': 'page hidden - payment-ct10',
        'wait-screen':  'page hidden - wait screen',
    },
    // --- Static (one-off) ---
    static: {
        CONNECTING_SUCCESS:                  'connecting - success',
        CONNECTING_FAILURE:                  'connecting - failure',
        PRODUCT_SELECTED:                    'product selected',
        PRODUCT_REJECTED_BY_DEVICE:          'product rejected by device',
        ORDER_COMPLETED:                     'order completed',
        PAYMENT_FAILED_ERROR:                'payment failed - error',
        PAYMENT_FAILED_APPLE_SERVER_REQUEST: 'payment failed - Apple - server request',
        // Tranzzo issuer asked for 3DS and the buyer is sent to the bank (SWRN-2094).
        PAYMENT_REDIRECTED_3DS_GOOGLE:       'payment redirected to 3DS - Google',
        // Loyalty mini-game (SWRN-1898). Win and loss are separate names, so
        // the outcome needs no payload — the score is deliberately not logged
        // (`timeout_value` means «seconds left», and a score is not that).
        GAME_BANNER_CLICKED:                 'game banner clicked',
        GAME_FINISHED_WIN:                   'game finished - win',
        GAME_FINISHED_LOSS:                  'game finished - loss',
        GAME_RETURNED_TO_SUCCESS:            'game returned to success',
    },
    // --- Timeouts (type: 'timeout') ---
    timeout: {
        'connecting':      'connecting',
        'select-product':  'select product',
        'wait-screen':     'wait screen',
        'snack':           'snack',
        'pulse':           'pulse',
        'water':           'water',
        'car-wash':        'car-wash',
        'payment':         'payment',
        'payment-ct5':     'payment-ct5',
        'payment-popcorn': 'payment-popcorn',
    },
    // --- WaitScreen timeouts, keyed by the action that redirected there ---
    // (type: 'timeout'). WaitScreen is reached from several flows; without a
    // source the timeout can't tell us *which* action stalled. The source is
    // passed via React-Router location state at each navigation into /wait.
    waitTimeout: {
        apple:     'wait screen - payment by apple',
        google:    'wait screen - payment by google',
        mypos:     'wait screen - payment by mypos',
        emerchant: 'wait screen - payment by emerchant',
        loyalty:   'wait screen - payment by loyalty',
        product:   'wait screen - after select product',
        recovery:  'wait screen - recovery',
    },
} as const;

// Typed keys consumed by hook signatures.
export type CancelPage            = keyof typeof EVENT_NAMES.cancel;
export type AmountSubmittedScreen = keyof typeof EVENT_NAMES.amountSubmitted;
export type InitiatedProvider     = keyof typeof EVENT_NAMES.paymentInitiated;
export type RedirectProvider      = keyof typeof EVENT_NAMES.paymentFailed;
export type NoUriProvider         = keyof typeof EVENT_NAMES.paymentFailedNoUri;
export type SuccessProvider       = keyof typeof EVENT_NAMES.paymentSuccess;
export type GooglePaySheetCloseReason = keyof typeof EVENT_NAMES.googlePaySheetClosed;
export type PageHiddenScreen      = keyof typeof EVENT_NAMES.pageHidden;
export type TimeoutScreen         = keyof typeof EVENT_NAMES.timeout;
export type WaitSource            = keyof typeof EVENT_NAMES.waitTimeout;

// Derived value unions — what `SendArgs.name` becomes.
type FlattenValues<T> = T extends Record<string, infer V> ? V : never;
export type EventName = FlattenValues<
    | typeof EVENT_NAMES.cancel
    | typeof EVENT_NAMES.amountSubmitted
    | typeof EVENT_NAMES.paymentInitiated
    | typeof EVENT_NAMES.paymentFailed
    | typeof EVENT_NAMES.paymentFailedNoUri
    | typeof EVENT_NAMES.paymentSuccess
    | typeof EVENT_NAMES.googlePaySheetClosed
    | typeof EVENT_NAMES.pageHidden
    | typeof EVENT_NAMES.static
>;
export type TimeoutName = FlattenValues<
    typeof EVENT_NAMES.timeout | typeof EVENT_NAMES.waitTimeout
>;

// Dev-only guard: every emitted name must be unique so ClickHouse can attribute
// a record to exactly one call site. Adding a duplicate value now fails fast at
// module load in dev instead of silently colliding in the funnel.
if (IS_DEV) {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const family of Object.values(EVENT_NAMES)) {
        for (const value of Object.values(family)) {
            if (seen.has(value)) {
                duplicates.add(value);
            }
            seen.add(value);
        }
    }
    if (duplicates.size > 0) {
        throw new Error(
            `EVENT_NAMES has duplicate timeout_name values: ${[...duplicates].join(', ')}. ` +
                'Each name must map to a single call site.',
        );
    }
}
